import { Typography, Grid, Divider, IconButton } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';

import { RootState } from '../../../shared/state/root-reducer';
import { AppDispatch } from '../../../shared/state/store';
import AlertBoxes from '../../../admissions-advisor/components/vob/alert-box';
import { getAlerts } from '@finpay-development/shared-platform/lib/esm/meta-data/state/meta-data-thunk';
import { Alert } from '@finpay-development/shared-platform/lib/esm/meta-data/models/metaData';
import { EditAlertsModal } from './edit-alerts-modal';
import { checkPermissions } from '@finpay-development/shared-platform/lib/esm/security/components/access-control';
import { RolePageNames } from '@finpay-development/shared-platform/lib/esm/security/model/role-page-names';
import {commentsPage} from "@finpay-development/shared-platform/lib/esm/shared/enums";
import Comments from "../../../implementation-specialist/components/implementation-clients/details/comments";

interface EstimatesSidebarProps {
    clientId?: number;
    clientEmployerId?: number | null;
    payorId?: number | null;
    groupNumber?: string;
    memberId?: string;
}

function EstimatesSidebar({
                              clientId,
                              clientEmployerId,
                              payorId,
                              groupNumber,
                              memberId
                          }: EstimatesSidebarProps) {
    const dispatch = useDispatch<AppDispatch>();

    const [isEditAlertsModalOpen, setIsEditAlertsModalOpen] = useState(false);
    const [payerAlerts, setPayerAlerts] = useState<Alert[]>([]);
    const [employerAlerts, setEmployerAlerts] = useState<Alert[]>([]);
    const [groupAlerts, setGroupAlerts] = useState<Alert[]>([]);
    const [prefixAlerts, setPrefixAlerts] = useState<Alert[]>([]);

    const clientAlerts = useSelector((state: RootState) => state.metaData.alerts);
    const clientEmployers = useSelector((state: RootState) => state.metaData.employers);
    const payorsMap = useSelector((state: RootState) => state.adminContext.adminConfigurationContext.payersMap);
    const userRolePages = useSelector((state: RootState) => state.userContext.userProfile.userRole.userRolePages);
    const canEditAlerts = checkPermissions(userRolePages, RolePageNames.Estimates, "Client Alerts", false);
    
    const selectedPatient = useSelector(
        (state: RootState) => state.patientContext.selectedPatient
    );
    
    const hasPatient = !!selectedPatient?.patientId;
    const shouldShowComments = hasPatient;

    const updateAlert = (clientId: number, selectedEmployerId: number | null, selectPayerId: number | null, selectGroupNumber: string, policyNumber: string) => {
        const alerts = clientAlerts[clientId];
        if (alerts) {
            const payerAlerts = (clientAlerts[clientId].payer ?? []).filter((payerAlert: Alert) => {
                return payerAlert.payor_id === selectPayerId;
            });

            const groupAlerts = (clientAlerts[clientId].group ?? []).filter((groupAlert: Alert) => {
                return groupAlert?.group_code === selectGroupNumber;
            });

            const employerAlerts = (clientAlerts[clientId].employer ?? []).filter((employerAlert: Alert) => {
                return employerAlert.client_employer_id === selectedEmployerId;
            });

            let newPrefixAlerts: Alert[] = [];
            const prefixes = clientAlerts[clientId].prefix ?? [];

            for (let i = 0; i < prefixes.length; i++) {
                const prefixAlert = prefixes[i];
                if (!prefixAlert.prefix) continue;
                const subStringEndingIndex = prefixAlert.prefix.length;
                const policyNumberSubString = policyNumber?.substring(0, subStringEndingIndex);
                const isAMatch = policyNumberSubString === prefixAlert.prefix;

                if (isAMatch) {
                    if (newPrefixAlerts.length === 0) {
                        newPrefixAlerts.push(prefixAlert);
                        continue;
                    }

                    const existingPrefixLen = newPrefixAlerts[0].prefix?.length ?? 0;
                    if (existingPrefixLen === prefixAlert.prefix.length) {
                        newPrefixAlerts.push(prefixAlert);
                        continue;
                    }

                    if (existingPrefixLen < prefixAlert.prefix.length) {
                        newPrefixAlerts = [prefixAlert];
                        continue;
                    }
                }
            }

            setPayerAlerts(payerAlerts);
            setEmployerAlerts(employerAlerts);
            setGroupAlerts(groupAlerts);
            setPrefixAlerts(newPrefixAlerts);
        } else if (!clientAlerts[clientId]) {
            dispatch(getAlerts(clientId));
        } else {
            setPayerAlerts([]);
            setEmployerAlerts([]);
            setGroupAlerts([]);
            setPrefixAlerts([]);
        }
    };

    useEffect(() => {
        if (clientId) {
            updateAlert(clientId, clientEmployerId || null, payorId || null, groupNumber || '', memberId || '');
        }else{
            setPayerAlerts([]);
            setEmployerAlerts([]);
            setGroupAlerts([]);
            setPrefixAlerts([]);
        }
    }, [clientId, clientEmployerId, payorId, groupNumber, memberId, clientAlerts]);

    return (
        <div>
            <Grid container justifyContent="space-between" alignItems="center">
                <Grid item>
                    <Typography variant="subtitle1">Alerts</Typography>
                </Grid>
                {canEditAlerts && (
                    <Grid item>
                        <IconButton size="small" onClick={() => setIsEditAlertsModalOpen(true)}>
                            <EditIcon fontSize="small" />
                        </IconButton>
                    </Grid>
                )}
            </Grid>
            <Divider className="mb-4" />
            <EditAlertsModal
                open={isEditAlertsModalOpen}
                onClose={() => setIsEditAlertsModalOpen(false)}
                clientId={clientId}
                payerAlerts={payerAlerts}
                employerAlerts={employerAlerts}
                groupAlerts={groupAlerts}
                prefixAlerts={prefixAlerts}
                currentPayerName={payorId ? (payorsMap?.get(payorId)?.payorName ?? payerAlerts[0]?.payor_name ?? null) : null}
                currentPrefix={memberId || null}
                currentGroupCode={groupNumber || null}
                currentEmployerName={
                    clientEmployerId
                        ? (clientId != null ? clientEmployers[clientId]?.find(e => e.clientEmployerId === clientEmployerId)?.employerName : undefined)
                            ?? employerAlerts[0]?.employer_name
                            ?? null
                        : null
                }
                payerAlertRefId={payorId != null ? String(payorId) : null}
                employerAlertRefId={clientEmployerId != null ? String(clientEmployerId) : null}
                prefixValue={prefixAlerts[0]?.prefix ?? memberId ?? null}
                groupCodeValue={groupNumber || null}
            />
            {(payerAlerts.length > 0 || employerAlerts.length > 0 ||
                groupAlerts.length > 0 || prefixAlerts.length > 0) ? (
                <>
                    <AlertBoxes alerts={payerAlerts}/>
                    <AlertBoxes alerts={employerAlerts}/>
                    <AlertBoxes alerts={groupAlerts}/>
                    <AlertBoxes alerts={prefixAlerts}/>
                </>
            ) : (
                <Typography variant="subtitle2">No Alerts</Typography>
            )}
            {shouldShowComments && (
                <>
                    <Divider className="mb-4" />
                    <Comments page={commentsPage.patients} />
                </>
            )}
        </div>
    );
}

export default EstimatesSidebar;